import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { LocaleProvider } from './contexts/LocaleContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { BrandingProvider } from './contexts/BrandingContext';
import { MuiThemeIntegration } from './contexts/MuiThemeIntegration';
import { ToastProvider } from './contexts/ToastContext';
import { MaintenanceProvider, useMaintenance } from './contexts/MaintenanceContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AssistantProvider } from './contexts/AssistantContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PermissionProtectedRoute } from './components/PermissionProtectedRoute';
import { RoleProtectedRoute } from './components/RoleProtectedRoute';
import { SystemDocumentationRoute } from './components/SystemDocumentationRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { MainLayout } from './layouts/MainLayout';
import { MaintenancePage } from './pages/MaintenancePage';
import { VersionHistoryPage } from './pages/VersionHistoryPage';

/**
 * Wraps React.lazy with automatic reload on chunk load failure (stale deployment).
 * If the dynamic import fails, reloads the page once to fetch fresh assets.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithRetry(factory: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(async () => {
    const reloadKey = 'chunk-reload-attempted';
    try {
      const component = await factory();
      sessionStorage.removeItem(reloadKey);
      return component;
    } catch (error) {
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, Date.now().toString());
        window.location.reload();
        return { default: () => null };
      }
      sessionStorage.removeItem(reloadKey);
      throw error;
    }
  });
}

const HomePage = lazyWithRetry(async () => {
  const module = await import('./pages/HomePage');
  return { default: module.HomePage };
});

const LoginPage = lazyWithRetry(async () => {
  const module = await import('./pages/LoginPage');
  return { default: module.LoginPage };
});

const RegisterPage = lazyWithRetry(async () => {
  const module = await import('./pages/RegisterPage');
  return { default: module.RegisterPage };
});

const ForgotPasswordPage = lazyWithRetry(async () => {
  const module = await import('./pages/ForgotPasswordPage');
  return { default: module.ForgotPasswordPage };
});

const ResetPasswordPage = lazyWithRetry(async () => {
  const module = await import('./pages/ResetPasswordPage');
  return { default: module.ResetPasswordPage };
});

const AcceptInvitationPage = lazyWithRetry(async () => {
  const module = await import('./pages/AcceptInvitationPage');
  return { default: module.AcceptInvitationPage };
});

const DashboardPage = lazyWithRetry(async () => {
  const module = await import('./pages/DashboardPage');
  return { default: module.DashboardPage };
});

const ReportsPage = lazyWithRetry(async () => {
  const module = await import('./pages/ReportsPage');
  return { default: module.ReportsPage };
});

const OperationalInsightsPage = lazyWithRetry(async () => {
  const module = await import('./pages/OperationalInsightsPage');
  return { default: module.OperationalInsightsPage };
});

const IncidentsListPage = lazyWithRetry(async () => {
  const module = await import('./pages/IncidentsListPage');
  return { default: module.IncidentsListPage };
});

const IncidentsWithSidebarPage = lazyWithRetry(async () => {
  const module = await import('./pages/IncidentsWithSidebarPage');
  return { default: module.IncidentsWithSidebarPage };
});

const AllIncidentsPage = lazyWithRetry(async () => {
  const module = await import('./pages/AllIncidentsPage');
  return { default: module.AllIncidentsPage };
});

const EventsListPage = lazyWithRetry(async () => {
  const module = await import('./pages/EventsListPage');
  return { default: module.EventsListPage };
});

const EpisodesPage = lazyWithRetry(async () => {
  const module = await import('./pages/EpisodesPage');
  return { default: module.EpisodesPage };
});

const EventsDashboardPage = lazyWithRetry(async () => {
  const module = await import('./pages/EventsDashboardPage');
  return { default: module.EventsDashboardPage };
});

const NotificationCenterPage = lazyWithRetry(async () => {
  const module = await import('./pages/NotificationCenterPage');
  return { default: module.NotificationCenterPage };
});

const EventDetailPage = lazyWithRetry(async () => {
  const module = await import('./pages/EventDetailPage');
  return { default: module.EventDetailPage };
});

const EpisodeDetailPage = lazyWithRetry(async () => {
  const module = await import('./pages/EpisodeDetailPage');
  return { default: module.EpisodeDetailPage };
});

const IngestionEndpointsPage = lazyWithRetry(async () => {
  const module = await import('./pages/IngestionEndpointsPage');
  return { default: module.IngestionEndpointsPage };
});

const CreateEndpointPage = lazyWithRetry(async () => {
  const module = await import('./pages/CreateEndpointPage');
  return { default: module.CreateEndpointPage };
});

const EndpointDetailPage = lazyWithRetry(async () => {
  const module = await import('./pages/EndpointDetailPage');
  return { default: module.EndpointDetailPage };
});

const CustomersListPage = lazyWithRetry(async () => {
  const module = await import('./pages/CustomersListPage');
  return { default: module.CustomersListPage };
});

const CreateCustomerPage = lazyWithRetry(async () => {
  const module = await import('./pages/CreateCustomerPage');
  return { default: module.CreateCustomerPage };
});

const CustomerDetailPage = lazyWithRetry(async () => {
  const module = await import('./pages/CustomerDetailPage');
  return { default: module.CustomerDetailPage };
});

const ServiceBusMonitoringPage = lazyWithRetry(async () => {
  const module = await import('./pages/ServiceBusMonitoringPage');
  return { default: module.ServiceBusMonitoringPage };
});

const SystemJobsPage = lazyWithRetry(async () => {
  const module = await import('./pages/SystemJobsPage');
  return { default: module.SystemJobsPage };
});

const SystemAssistantUsagePage = lazyWithRetry(async () => {
  const module = await import('./pages/SystemAssistantUsagePage');
  return { default: module.SystemAssistantUsagePage };
});

const AdminUsersPage = lazyWithRetry(async () => {
  const module = await import('./pages/AdminUsersPage');
  return { default: module.AdminUsersPage };
});
const UserManagementPage = lazyWithRetry(async () => {
  const module = await import('./pages/UserManagementPage');
  return { default: module.UserManagementPage };
});

const PriorityConfigPage = lazyWithRetry(async () => {
  const module = await import('./pages/PriorityConfigPage');
  return { default: module.PriorityConfigPage };
});

const ReasonConfigPage = lazyWithRetry(async () => {
  const module = await import('./pages/ReasonConfigPage');
  return { default: module.ReasonConfigPage };
});

const ItilConfigPage = lazyWithRetry(async () => {
  const module = await import('./pages/ItilConfigPage');
  return { default: module.ItilConfigPage };
});

const SystemItilConfigPage = lazyWithRetry(async () => {
  const module = await import('./pages/SystemItilConfigPage');
  return { default: module.SystemItilConfigPage };
});

const SystemSettingsPage = lazyWithRetry(async () => {
  const module = await import('./pages/SystemSettingsPage');
  return { default: module.SystemSettingsPage };
});

const SystemInfoPage = lazyWithRetry(async () => {
  const module = await import('./pages/SystemInfoPage');
  return { default: module.SystemInfoPage };
});

const TenantSettingsPage = lazyWithRetry(async () => {
  const module = await import('./pages/TenantSettingsPage');
  return { default: module.TenantSettingsPage };
});

const SlaPlatformDefaultsPage = lazyWithRetry(async () => {
  const module = await import('./pages/SlaPlatformDefaultsPage');
  return { default: module.SlaPlatformDefaultsPage };
});

const AIUsagePage = lazyWithRetry(async () => {
  const module = await import('./pages/AIUsagePage');
  return { default: module.AIUsagePage };
});

const TenantBrandingPage = lazyWithRetry(async () => {
  const module = await import('./pages/TenantBrandingPage');
  return { default: module.TenantBrandingPage };
});

const TeamsIntegrationPage = lazyWithRetry(async () => {
  const module = await import('./pages/settings/TeamsIntegrationPage');
  return { default: module.TeamsIntegrationPage };
});

const RolesManagementPage = lazyWithRetry(async () => {
  const module = await import('./pages/RolesManagementPage');
  return { default: module.RolesManagementPage };
});

const RoleAssignmentsPage = lazyWithRetry(async () => {
  const module = await import('./pages/RoleAssignmentsPage');
  return { default: module.RoleAssignmentsPage };
});

const TeamsManagementPage = lazyWithRetry(async () => {
  const module = await import('./pages/TeamsManagementPage');
  return { default: module.TeamsManagementPage };
});

const IntegrationsPageEnhanced = lazyWithRetry(async () => {
  const module = await import('./pages/IntegrationsPageEnhanced');
  return { default: module.IntegrationsPageEnhanced };
});

const ConfiguredIntegrationsPage = lazyWithRetry(async () => {
  const module = await import('./pages/ConfiguredIntegrationsPage');
  return { default: module.ConfiguredIntegrationsPage };
});

const DiscoveryJobsPage = lazyWithRetry(async () => {
  const module = await import('./pages/DiscoveryJobsPage');
  return { default: module.DiscoveryJobsPage };
});

const DiscoveryJobDetailPage = lazyWithRetry(async () => {
  const module = await import('./pages/DiscoveryJobDetailPage');
  return { default: module.DiscoveryJobDetailPage };
});

const SystemLogsPage = lazyWithRetry(async () => {
  const module = await import('./pages/SystemLogsPage');
  return { default: module.SystemLogsPage };
});

const PerformanceDashboardPage = lazyWithRetry(async () => {
  const module = await import('./pages/PerformanceDashboardPage');
  return { default: module.PerformanceDashboardPage };
});

const EmailHistoryPage = lazy(() => import('./pages/EmailHistoryPage'));

const OnCallSchedulesPage = lazy(() => import('./pages/OnCallSchedulesPage'));
const OnCallScheduleDetailPage = lazy(() => import('./pages/OnCallScheduleDetailPage'));
const OnCallCalendarView = lazy(() => import('./pages/OnCallCalendarView'));
const EscalationPoliciesPage = lazy(() => import('./pages/EscalationPoliciesPage'));
const HolidayCalendarsPage = lazy(() => import('./pages/HolidayCalendarsPage'));
const SlaSchedulesPage = lazy(() => import('./pages/SlaSchedulesPage'));

const UserProfilePage = lazyWithRetry(async () => {
  const module = await import('./pages/UserProfilePage');
  return { default: module.UserProfilePage };
});

const UserDetailPage = lazyWithRetry(async () => {
  const module = await import('./pages/UserDetailPage');
  return { default: module.UserDetailPage };
});

const AutoCloseMonitoringPage = lazyWithRetry(async () => {
  const module = await import('./pages/AutoCloseMonitoringPage');
  return { default: module.AutoCloseMonitoringPage };
});

const SignInLogsPage = lazyWithRetry(async () => {
  const module = await import('./pages/SignInLogsPage');
  return { default: module.SignInLogsPage };
});

const FeedbackBugsPage = lazyWithRetry(async () => {
  const module = await import('./pages/FeedbackBugsPage');
  return { default: module.FeedbackBugsPage };
});

const OAuthCallbackPage = lazyWithRetry(async () => {
  const module = await import('./pages/OAuthCallbackPage');
  return { default: module.OAuthCallbackPage };
});

const PublicSharedReportPage = lazyWithRetry(async () => {
  const module = await import('./pages/PublicSharedReportPage');
  return { default: module.PublicSharedReportPage };
});

const DocumentationPage = lazyWithRetry(async () => {
  const module = await import('./pages/DocumentationPage');
  return { default: module.DocumentationPage };
});

const SystemDocumentationPage = lazyWithRetry(async () => {
  const module = await import('./pages/SystemDocumentationPage');
  return { default: module.SystemDocumentationPage };
});

const CmdbDashboardPage = lazyWithRetry(async () => {
  const module = await import('./pages/CmdbDashboardPage');
  return { default: module.CmdbDashboardPage };
});

const CmdbItemsListPage = lazyWithRetry(async () => {
  const module = await import('./pages/CmdbItemsListPage');
  return { default: module.CmdbItemsListPage };
});

const CmdbItemCreatePage = lazyWithRetry(async () => {
  const module = await import('./pages/CmdbItemCreatePage');
  return { default: module.CmdbItemCreatePage };
});

const CmdbItemDetailPage = lazyWithRetry(async () => {
  const module = await import('./pages/CmdbItemDetailPage');
  return { default: module.CmdbItemDetailPage };
});

const CmdbItemEditPage = lazyWithRetry(async () => {
  const module = await import('./pages/CmdbItemEditPage');
  return { default: module.CmdbItemEditPage };
});

const CmdbClassesPage = lazyWithRetry(async () => {
  const module = await import('./pages/CmdbClassesPage');
  return { default: module.CmdbClassesPage };
});

const CmdbViewBuilderPage = lazyWithRetry(async () => {
  const module = await import('./pages/CmdbViewBuilderPage');
  return { default: module.CmdbViewBuilderPage };
});

const CmdbCatalogPage = lazyWithRetry(async () => {
  const module = await import('./pages/CmdbCatalogPage');
  return { default: module.CmdbCatalogPage };
});

const CmdbDependencyMapPage = lazyWithRetry(async () => {
  const module = await import('./pages/CmdbDependencyMapPage');
  return { default: module.CmdbDependencyMapPage };
});

const CmdbTagMappingsPage = lazyWithRetry(async () => {
  const module = await import('./pages/CmdbTagMappingsPage');
  return { default: module.CmdbTagMappingsPage };
});

const CmdbAzureHierarchyPage = lazyWithRetry(async () => {
  const module = await import('./pages/CmdbAzureHierarchyPage');
  return { default: module.CmdbAzureHierarchyPage };
});

const CmdbPredefinedTagsPage = lazyWithRetry(async () => {
  const module = await import('./pages/CmdbPredefinedTagsPage');
  return { default: module.CmdbPredefinedTagsPage };
});

const PipelineMetricsPage = lazyWithRetry(async () => {
  const module = await import('./pages/PipelineMetricsPage');
  return { default: module.PipelineMetricsPage };
});

const TableBrowserPage = lazyWithRetry(async () => {
  const module = await import('./pages/TableBrowserPage');
  return { default: module.TableBrowserPage };
});

const SuppressionRulesListPage = lazyWithRetry(async () => {
  const module = await import('./pages/SuppressionRulesListPage');
  return { default: module.SuppressionRulesListPage };
});

const CreateSuppressionRulePage = lazyWithRetry(async () => {
  const module = await import('./pages/CreateSuppressionRulePage');
  return { default: module.CreateSuppressionRulePage };
});

const SuppressionRuleDetailPage = lazyWithRetry(async () => {
  const module = await import('./pages/SuppressionRuleDetailPage');
  return { default: module.SuppressionRuleDetailPage };
});

const CorrelationRulesListPage = lazyWithRetry(async () => {
  const module = await import('./pages/CorrelationRulesListPage');
  return { default: module.CorrelationRulesListPage };
});

const CreateCorrelationRulePage = lazyWithRetry(async () => {
  const module = await import('./pages/CreateCorrelationRulePage');
  return { default: module.CreateCorrelationRulePage };
});

const CorrelationRuleDetailPage = lazyWithRetry(async () => {
  const module = await import('./pages/CorrelationRuleDetailPage');
  return { default: module.CorrelationRuleDetailPage };
});

const ScriptLibraryPage = lazyWithRetry(async () => {
  const module = await import('./pages/ScriptLibraryPage');
  return { default: module.ScriptLibraryPage };
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Never retry auth errors — let the interceptor handle redirect to login
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) return false;
        return failureCount < 1;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes (increased from 30s for better caching)
      gcTime: 10 * 60 * 1000,   // 10 minutes (formerly cacheTime in v4)
    },
  },
});

const PageLoadingFallback = () => (
  <div className="flex justify-center items-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>
);

/** Renders the maintenance page when the backend is unreachable. */
function MaintenanceGate({ children }: { children: React.ReactNode }) {
  const { isMaintenanceMode } = useMaintenance();
  if (isMaintenanceMode) return <MaintenancePage />;
  return <>{children}</>;
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <MaintenanceProvider>
          <AuthProvider>
            <LocaleProvider>
              <ThemeProvider>
                <BrandingProvider>
                  <MuiThemeIntegration>
                  <ToastProvider>
                    <NotificationProvider>
                      <AssistantProvider>
                        <BrowserRouter>
                          <SidebarProvider>
                          <MaintenanceGate>
                            <Suspense fallback={<PageLoadingFallback />}>
                              <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
              <Route path="/auth/callback" element={<OAuthCallbackPage />} />
              {/* Phase 10 — public shared-report viewer (anonymous, rate-limited server-side). */}
              <Route path="/r/:token" element={<PublicSharedReportPage />} />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <DashboardPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/reports"
                element={
                  <ProtectedRoute>
                    <MainLayout constrainContent={false}>
                      <ReportsPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/operational-insights"
                element={
                  <ProtectedRoute>
                    <MainLayout constrainContent={false}>
                      <OperationalInsightsPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/docs"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <DocumentationPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/docs/system/*"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SystemDocumentationRoute>
                        <SystemDocumentationPage />
                      </SystemDocumentationRoute>
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/docs/*"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <DocumentationPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/incidents/all"
                element={
                  <ProtectedRoute>
                    <MainLayout constrainContent={false}>
                      <AllIncidentsPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/incidents/*"
                element={
                  <ProtectedRoute>
                    <MainLayout constrainContent={false}>
                      <IncidentsWithSidebarPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/incidents-legacy"
                element={
                  <ProtectedRoute>
                    <MainLayout constrainContent={false}>
                      <IncidentsListPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/events"
                element={
                  <ProtectedRoute>
                    <MainLayout constrainContent={false}>
                      <EventsListPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/episodes"
                element={
                  <ProtectedRoute>
                    <MainLayout constrainContent={false}>
                      <EpisodesPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/events-dashboard"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <EventsDashboardPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <NotificationCenterPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/pipeline-metrics"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <PipelineMetricsPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/version-history"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <VersionHistoryPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/events/:id"
                element={
                  <ProtectedRoute>
                    <MainLayout constrainContent={false}>
                      <div className="px-4 sm:px-6 lg:px-8 py-6">
                        <EventDetailPage />
                      </div>
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/episodes/:id"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <EpisodeDetailPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/ingestion-endpoints"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <IngestionEndpointsPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/ingestion-endpoints/new"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CreateEndpointPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/ingestion-endpoints/:id"
                element={
                  <ProtectedRoute>
                    <MainLayout constrainContent={false}>
                      <EndpointDetailPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

                  <Route
                    path="/customers"
                    element={
                      <ProtectedRoute>
                        <MainLayout constrainContent={false}>
                      <CustomersListPage />
                    </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/tables"
                    element={
                      <ProtectedRoute>
                        <MainLayout constrainContent={false}>
                          <TableBrowserPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/scripts"
                    element={
                      <ProtectedRoute>
                        <MainLayout constrainContent={false}>
                          <ScriptLibraryPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

              <Route
                path="/customers/new"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CreateCustomerPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/customers/:id"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CustomerDetailPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/suppression-rules"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <SuppressionRulesListPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/suppression-rules/create"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <CreateSuppressionRulePage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/suppression-rules/:id"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <SuppressionRuleDetailPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/correlation-rules"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <CorrelationRulesListPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/correlation-rules/create"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <CreateCorrelationRulePage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/correlation-rules/:id"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <CorrelationRuleDetailPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboards/assets"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CmdbDashboardPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/cmdb"
                element={<Navigate to="/cmdb/items" replace />}
              />

              <Route
                path="/cmdb/items"
                element={
                  <ProtectedRoute>
                    <MainLayout constrainContent={false}>
                      <CmdbItemsListPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/cmdb/azure-hierarchy"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CmdbAzureHierarchyPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/cmdb/items/new"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CmdbItemCreatePage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/cmdb/items/:id/graph"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CmdbDependencyMapPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/cmdb/items/:id/edit"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CmdbItemEditPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/cmdb/items/:id"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CmdbItemDetailPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/cmdb/classes"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CmdbClassesPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/cmdb/classes/:classId/views"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CmdbViewBuilderPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/cmdb/catalog"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CmdbCatalogPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/cmdb/tag-mappings"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CmdbTagMappingsPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/cmdb/predefined-tags"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CmdbPredefinedTagsPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/system-monitoring/service-bus"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['SystemAdmin', 'TenantAdmin']}>
                      <MainLayout>
                        <ServiceBusMonitoringPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/system-monitoring/system-jobs"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['SystemAdmin', 'TenantAdmin']}>
                      <MainLayout>
                        <SystemJobsPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/system-monitoring/auto-close"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['SystemAdmin', 'TenantAdmin']}>
                      <MainLayout>
                        <AutoCloseMonitoringPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/system-monitoring/assistant-usage"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['SystemAdmin']}>
                      <MainLayout>
                        <SystemAssistantUsagePage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              {/* Legacy route redirect for backwards compatibility */}
              <Route
                path="/service-bus"
                element={
                  <Navigate to="/system-monitoring/service-bus" replace />
                }
              />

              {/* Redirect old pipeline config route to endpoints */}
              <Route
                path="/admin/pipeline"
                element={<Navigate to="/ingestion-endpoints" replace />}
              />

              <Route
                path="/settings/email"
                element={<Navigate to="/settings/tenant" replace />}
              />

              <Route
                path="/admin/api-keys"
                element={<Navigate to="/settings/tenant" replace />}
              />

              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute>
                    <MainLayout constrainContent={false}>
                      <AdminUsersPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/users/management"
                element={
                  <ProtectedRoute>
                    <MainLayout constrainContent={false}>
                      <UserManagementPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <UserProfilePage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/users/:userId"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <UserDetailPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/auth/signin-logs"
                element={
                  <ProtectedRoute>
                    <MainLayout constrainContent={false}>
                      <SignInLogsPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/feedback-bugs"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['SystemAdmin']}>
                      <MainLayout constrainContent={false}>
                        <FeedbackBugsPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/priority-configuration"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <PriorityConfigPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/reason-configuration"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <ReasonConfigPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/itil-configuration"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <ItilConfigPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/system-itil-configuration"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['SystemAdmin']}>
                      <MainLayout>
                        <SystemItilConfigPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings/tenant"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['TenantAdmin', 'SystemAdmin']}
                    >
                      <MainLayout>
                        <TenantSettingsPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings/sla-platform-defaults"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['TenantAdmin', 'SystemAdmin']}
                    >
                      <PermissionProtectedRoute requiredPermission="settings.read">
                        <MainLayout>
                          <SlaPlatformDefaultsPage />
                        </MainLayout>
                      </PermissionProtectedRoute>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings/ai-usage"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['TenantAdmin', 'SystemAdmin']}
                    >
                      <MainLayout>
                        <AIUsagePage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings/tenant/assistant-usage"
                element={<Navigate to="/settings/ai-usage" replace />}
              />

              <Route
                path="/settings/branding"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['TenantAdmin', 'SystemAdmin']}
                    >
                      <MainLayout>
                        <TenantBrandingPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings/teams"
                element={
                  <ProtectedRoute>
                    <PermissionProtectedRoute
                      requiredPermission="teams_integration.view"
                    >
                      <MainLayout>
                        <TeamsIntegrationPage />
                      </MainLayout>
                    </PermissionProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/settings"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['SystemAdmin']}>
                      <MainLayout>
                        <SystemSettingsPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/system-info"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['SystemAdmin']}>
                      <MainLayout>
                        <SystemInfoPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/roles"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <RolesManagementPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/role-assignments"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <RoleAssignmentsPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/teams"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <TeamsManagementPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/integrations/teams-analytics"
                element={<Navigate to="/settings/teams?tab=analytics" replace />}
              />

              <Route
                path="/admin/on-call"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <OnCallSchedulesPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/on-call/:id"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <OnCallScheduleDetailPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/on-call/calendar"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <OnCallCalendarView />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/escalation-policies"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <EscalationPoliciesPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/holiday-calendars"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <HolidayCalendarsPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/sla-schedules"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <SlaSchedulesPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/integrations"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <IntegrationsPageEnhanced />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/integrations/configured"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout>
                        <ConfiguredIntegrationsPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/integrations/discovery-jobs"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout constrainContent={false}>
                        <DiscoveryJobsPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/integrations/discovery-jobs/:sourceId/:jobId"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute
                      allowedRoles={['SystemAdmin', 'TenantAdmin']}
                    >
                      <MainLayout constrainContent={false}>
                        <DiscoveryJobDetailPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />



              <Route
                path="/admin/system-logs"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['SystemAdmin']}>
                      <MainLayout constrainContent={false}>
                        <SystemLogsPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/performance"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['SystemAdmin']}>
                      <MainLayout constrainContent={false}>
                        <PerformanceDashboardPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/email-history"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute allowedRoles={['SystemAdmin']}>
                      <MainLayout constrainContent={false}>
                        <EmailHistoryPage />
                      </MainLayout>
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />

                    <Route path="*" element={<Navigate to="/" replace />} />
                              </Routes>
                            </Suspense>
                          </MaintenanceGate>
                          </SidebarProvider>
                        </BrowserRouter>
                      </AssistantProvider>
                    </NotificationProvider>
                  </ToastProvider>
                  </MuiThemeIntegration>
                </BrandingProvider>
              </ThemeProvider>
            </LocaleProvider>
      </AuthProvider>
    </MaintenanceProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
